import React, { useId } from 'react';
import { Box } from '@mui/material';
import { chart } from '../../theme/chart';

interface Props {
  /** shares of the limit, 0–100, already clamped so they never overflow */
  spentPct: number;
  committedPct: number;
  /** green within the limit, orange once spent + committed passes it */
  hue: string;
  /** where the month stands today, 0–1; undefined for months other than the current one */
  pacePct?: number;
  headline: string;
  subline: string;
  ariaLabel: string;
}

const SIZE = 220;
const CENTER = SIZE / 2;
const RADIUS = 82;
const WIDTH = 16;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The month's budget as one ring: the dashboard's bar bent into a circle,
 * with the same three shares — spent (solid), committed to open bills
 * (hatched, same hue), still free (track) — and the headline in the middle.
 * A tick on the rim marks where the month is today.
 */
export const BudgetRing: React.FC<Props> = ({ spentPct, committedPct, hue, pacePct, headline, subline, ariaLabel }) => {
  const hatchId = `budget-hatch-${useId().replace(/:/g, '')}`;
  const spentLength = (CIRCUMFERENCE * spentPct) / 100;
  const committedLength = (CIRCUMFERENCE * committedPct) / 100;
  // A 2px gap between the two shares, as on the bar.
  const committedOffset = spentLength > 0 && committedLength > 0 ? spentLength + 2 : spentLength;
  const angle = pacePct === undefined ? null : -Math.PI / 2 + 2 * Math.PI * Math.min(Math.max(pacePct, 0), 1);
  const tick = angle === null
    ? null
    : {
        x1: CENTER + Math.cos(angle) * (RADIUS + WIDTH / 2 + 3),
        y1: CENTER + Math.sin(angle) * (RADIUS + WIDTH / 2 + 3),
        x2: CENTER + Math.cos(angle) * (RADIUS + WIDTH / 2 + 12),
        y2: CENTER + Math.sin(angle) * (RADIUS + WIDTH / 2 + 12),
      };

  return (
    <Box component="svg" viewBox={`0 0 ${SIZE} ${SIZE}`} sx={{ width: SIZE, maxWidth: '100%', height: 'auto', display: 'block' }} role="img" aria-label={ariaLabel}>
      <defs>
        <pattern id={hatchId} patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
          <rect width="4" height="8" fill={hue} opacity="0.6" />
        </pattern>
      </defs>
      <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke={chart.track} strokeWidth={WIDTH} />
      {spentLength > 0 && (
        <circle
          cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke={hue} strokeWidth={WIDTH}
          strokeDasharray={`${spentLength} ${CIRCUMFERENCE}`}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
        />
      )}
      {committedLength > 0 && (
        <circle
          cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke={`url(#${hatchId})`} strokeWidth={WIDTH}
          strokeDasharray={`${Math.max(committedLength - 2, 0)} ${CIRCUMFERENCE}`}
          strokeDashoffset={-committedOffset}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
        />
      )}
      {tick && <line x1={tick.x1} y1={tick.y1} x2={tick.x2} y2={tick.y2} stroke={chart.inkSecondary} strokeWidth={2} />}
      <text x={CENTER} y={CENTER + 2} textAnchor="middle" fontSize={28} fontWeight={600} fill={hue === chart.spend ? chart.spend : chart.ink} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {headline}
      </text>
      <text x={CENTER} y={CENTER + 22} textAnchor="middle" fontSize={12} fill={chart.inkMuted}>
        {subline}
      </text>
    </Box>
  );
};
